import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageHeaderDropdownItem } from '../../../shared/page-header/page-header.model';
import { SharedModule } from '../../../shared/shared.module';
import { SubscriptionsModule } from '../../tariffs/subscriptions/subscriptions.module';
import { KkmInfoComponent } from './kkm-info/kkm-info.component';
import { KkmRegStatusBlockComponent } from './kkm-reg-status-block/kkm-reg-status-block.component';
import { KkmRetailPlaceInfoInfoComponent } from './kkm-retail-place-info/kkm-retail-place-info.component';
import { KkmSettingsStateService } from './services/kkm-settings-state.service';
import { KkmTariffManagementComponent } from './kkm-tariff-management/kkm-tariff-management.component';
import { KkmSettingsNavigationService } from './services/kkm-setting-navigation.service';
import { KkmNetworkInfoComponent } from './kkm-network-info/kkm-network-info.component';
import { KkmSettingsButtonsControlComponent } from './kkm-settings-buttons-control/kkm-settings-buttons-control.component';
import { RegStatus } from '../../../core/modules/retail-places/retail-paces.types';
import { KkmsApiService } from '../../../core/modules/kkms/kkms-api.service';
import { AvailableOperationsModel } from '../../../core/modules/kkms/kkms.types';

@UntilDestroy()
@Component({
  selector: 'tax-kkm-settings-page',
  templateUrl: './kkm-settings-page.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SharedModule,
    FormsModule,
    RouterLink,
    SubscriptionsModule,
    KkmInfoComponent,
    KkmRegStatusBlockComponent,
    KkmRetailPlaceInfoInfoComponent,
    KkmTariffManagementComponent,
    KkmNetworkInfoComponent,
    KkmSettingsButtonsControlComponent,
  ],
  providers: [KkmSettingsStateService],
})

export class KkmSettingsPageComponent {
  isAnyTariffManagementButtonVisible = false;
  isRegStatusBlockVisible = false;
  dropdownItems: PageHeaderDropdownItem[] = [];
  newSimKkmId: string | null = null;
  newKkmTitle = '';
  readonly kkm$ = this.kkmSettingsStateService.kkm$
    .pipe(
      tap((kkm) => {
        this.dropdownItems = this.kkmSettingsNavigationService.getDropdownNavigationItems(kkm);
        this.setAnyTariffManagementButtonVisible(kkm.availableOperations);
        this.setRegStatusBlockVisible(kkm.regStatus);
        this.newSimKkmId = kkm.network?.id ?? null;
        this.newKkmTitle = kkm.internalName;
      }),
    );

  constructor(
    private kkmsApiService: KkmsApiService,
    private kkmSettingsStateService: KkmSettingsStateService,
    private kkmSettingsNavigationService: KkmSettingsNavigationService,
  ) {}

  openToken(kkmId: number): void {
    this.kkmSettingsNavigationService.openTokenModal(kkmId);
  }

  saveNewKkmData(kkmId: number): void {
    this.kkmsApiService.updateKkm(kkmId, this.newKkmTitle, this.newSimKkmId).pipe(
      untilDestroyed(this),
    ).subscribe(() => {
      this.kkmSettingsStateService.kkmUpdated$.next();
    });
  }

  private setAnyTariffManagementButtonVisible(operations: AvailableOperationsModel): void {
    this.isAnyTariffManagementButtonVisible = operations.selectTariff || operations.activateTariff;
  }

  private setRegStatusBlockVisible(regStatus: RegStatus): void {
    this.isRegStatusBlockVisible = [
      RegStatus.Saved,
      RegStatus.SubmittedForRegistration,
      RegStatus.ReregistrationError,
      RegStatus.RequestToDeregisterHasBeenSent,
      RegStatus.AwaitingReturnToOfd,
      RegStatus.AwaitingTransferAnotherOfd,
      RegStatus.ErrorDisconnectingOfd,
      RegStatus.DisconnectedOfd,
    ].includes(regStatus);
  }
}
